import {
  DelayedSubscriptionSummaryDto,
  MissingBillingItem,
  OrganizationSummaryDto,
  PendingShopifyApprovalInfo,
  StripeSubscriptionSummaryDto,
  SubscriptionCollectionMethod,
} from "@intelligems/ig-types";
import {
  formatDate,
  formatDaysText,
  hadRecentTrialSubscription,
  latestTrialEnd,
} from "@intelligems/ig-utils";
import type { ButtonProps } from "@mui/material";
import { useGetOrganizationSummaryWithLoading } from "@src/api/organization-summaries/get-organization-summary-with-loading";
import { commitmentEndsTrial } from "@src/components/BillingSettings2026/workflow";
import { isModularOrganizationSummary } from "@src/utils/modular-billing-organization";
import {
  isTrialDeadlineAhead,
  resolveTrialDeadline,
  type TrialDeadline,
} from "@src/utils/trial-deadline";

type BillingActionType =
  | "navigate"
  | "stripe-billing"
  | "shopify-billing"
  | "shopify-approval";

type ButtonColor = ButtonProps["color"];
type ButtonVariant = ButtonProps["variant"];

export interface BillingStatusContent {
  cardMessage: string;
  cta: string | null;
  secondaryText: string;
  actionType: BillingActionType;
  buttonColor: ButtonColor;
  buttonVariant: ButtonVariant;
  missingBilling?: StripeSubscriptionSummaryDto | null;
  delayedSubscription?: DelayedSubscriptionSummaryDto;
  buttonBorder?: any;
  recoveryCommandId?: string;
  pendingShopifyApproval?: PendingShopifyApprovalInfo;
  isTrialReminder?: boolean;
}

const BILLING_NUDGE_POPUP_DELAY_MS = 30 * 60 * 1000;

export const isBillingNudgePopupInQuietWindow = (
  createdAtTs: number,
  nowMs: number = Date.now()
): boolean => nowMs - createdAtTs < BILLING_NUDGE_POPUP_DELAY_MS;

/**
 * Trial-deadline nudges escalate on discrete day boundaries as the deadline
 * nears. The card itself stays neutral; only the CTA carries the urgency.
 *
 *   > 8 days   outlined   |   4 < d <= 8   accent   |   0 < d <= 4   warning
 *   ends today  warning   |   ended / unknown   error
 *
 * Shared by every nudge that fronts a trial deadline. The final day is still a
 * live trial, so it keeps the warning band rather than the terminal error one.
 * Orgs with no trial deadline keep the terminal treatment.
 */
export const getTrialNudgeUrgency = (
  deadline: TrialDeadline
): Pick<BillingStatusContent, "buttonColor" | "buttonVariant"> => {
  if (deadline.state === "running") {
    if (deadline.remainingDays > 8) {
      return { buttonColor: "secondary", buttonVariant: "outlined" };
    }
    if (deadline.remainingDays > 4) {
      return { buttonColor: "primary", buttonVariant: "contained" };
    }
    return { buttonColor: "warning", buttonVariant: "contained" };
  }
  if (deadline.state === "ends_today") {
    return { buttonColor: "warning", buttonVariant: "contained" };
  }
  return { buttonColor: "error", buttonVariant: "contained" };
};

/**
 * A `trialing` subscription outlives its own deadline — Stripe transitions it
 * a beat after `trialEnd` passes, and the repair cron can lag further — so the
 * deadline elapses while the trial nudge is still on screen. Past the deadline
 * the countdown framing is wrong in both tense and substance, and every nudge
 * fronting a trial says so the same way.
 *
 * The countdown is worded to stay on one line in the nav drawer, which gives
 * the title under 200px before it truncates.
 */
export const trialDeadlineTitle = (deadline: TrialDeadline): string => {
  switch (deadline.state) {
    case "running":
      return `${formatDaysText(deadline.remainingDays)} left in trial`;
    case "ends_today":
      return "Trial ends today";
    case "ended":
      return "Trial ended";
    case "unknown":
      return "Trial active";
  }
};

const findMissingBilling = (
  missingBillings: StripeSubscriptionSummaryDto[],
  billingProvider: string
) => {
  return (
    missingBillings.find((b) => b.billingProvider === billingProvider) || null
  );
};

const trialEndedContent = (
  billingProvider: string,
  recentTrialSubscription: boolean,
  missingBillings: StripeSubscriptionSummaryDto[]
): BillingStatusContent => {
  const missingBilling = findMissingBilling(missingBillings, billingProvider);
  if (billingProvider === "stripe") {
    return {
      cardMessage: recentTrialSubscription
        ? "Trial ended"
        : "Add payment method",
      cta: "Add payment method",
      secondaryText: "Add payment to continue using Intelligems.",
      actionType: "stripe-billing" as const,
      missingBilling,
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }
  return {
    cardMessage: recentTrialSubscription
      ? "Trial ended"
      : "Approve Intelligems plan",
    cta: "Approve plan in Shopify",
    secondaryText: "Approve plan to continue using Intelligems.",
    actionType: "shopify-billing" as const,
    missingBilling,
    buttonColor: "error",
    buttonVariant: "contained",
  };
};

const trialEndedNoBillingContent = (): BillingStatusContent => {
  return {
    cardMessage: "Trial ended",
    cta: "Choose plan",
    secondaryText: "Select plan to continue using Intelligems.",
    actionType: "navigate" as const,
    buttonColor: "error",
    buttonVariant: "contained",
  };
};

const pastDueContent = (
  billingProvider: string,
  missingBillings: StripeSubscriptionSummaryDto[]
): BillingStatusContent => {
  const missingBilling = findMissingBilling(missingBillings, billingProvider);

  if (billingProvider === "shopify") {
    return {
      cardMessage: "Unprocessed Billing",
      cta: missingBillings.length > 0 ? "Approve plan in Shopify" : null,
      secondaryText:
        missingBillings.length > 0
          ? "Approve plan to continue using Intelligems."
          : "Waiting on Shopify for Plan Update. Contact support for further information.",
      actionType: "shopify-billing" as const,
      missingBilling,
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }

  return {
    cardMessage: "Unprocessed Billing",
    cta: missingBillings.length > 0 ? "Add payment method" : null,
    secondaryText:
      missingBillings.length > 0
        ? "Add payment method to continue using Intelligems."
        : "Pay invoice to continue using Intelligems.",
    actionType: "stripe-billing" as const,
    missingBilling,
    buttonColor: "error",
    buttonVariant: "contained",
  };
};

/**
 * The card cuts a long title off, so titles count whole days and the body
 * carries the date. The count never ticks: Built for Shopify bans countdown
 * timers and pressure language.
 */
const chooseTrialPlanCopy = (
  deadline: Extract<TrialDeadline, { state: "running" | "ends_today" }>
): Pick<BillingStatusContent, "cardMessage" | "secondaryText"> => {
  const endsOn = deadline.endsAtLabel;
  const remainingDays =
    deadline.state === "running" ? deadline.remainingDays : 0;
  const endsIn =
    remainingDays >= 1
      ? `Trial ends in ${formatDaysText(remainingDays)}`
      : "Trial ends today";

  if (remainingDays >= 8) {
    return {
      cardMessage: endsIn,
      secondaryText: `Choose the plan you want to continue on. Nothing is charged until ${endsOn}.`,
    };
  }
  if (remainingDays >= 3) {
    return {
      cardMessage: endsIn,
      secondaryText: `Choose your plan now and keep everything running when the trial ends on ${endsOn}.`,
    };
  }
  return {
    cardMessage: endsIn,
    secondaryText: `Choose a plan to keep your 4 modules running. Without a plan, they turn off on ${endsOn}.`,
  };
};

export const chooseTrialPlanContent = (
  deadline: Extract<TrialDeadline, { state: "running" | "ends_today" }>,
  missingBillings: StripeSubscriptionSummaryDto[]
): BillingStatusContent => ({
  ...chooseTrialPlanCopy(deadline),
  cta: "Choose plan",
  actionType: "shopify-billing" as const,
  missingBilling: findMissingBilling(missingBillings, "shopify"),
  ...getTrialNudgeUrgency(deadline),
  isTrialReminder: true,
});

export const noBillingActiveTrialContent = (
  billingProvider: string,
  deadline: TrialDeadline,
  missingBillings: StripeSubscriptionSummaryDto[]
): BillingStatusContent => {
  const stripeBillingProvider = billingProvider === "stripe";
  const missingBilling = findMissingBilling(missingBillings, billingProvider);

  // The same predicate the CTA urgency uses, so the copy cannot promise a
  // deadline the CTA has already given up on: an `unknown` deadline is styled
  // terminal, and "before trial ends" would name an end it cannot support.
  const hasLiveDeadline = isTrialDeadlineAhead(deadline);
  const secondaryText = stripeBillingProvider
    ? hasLiveDeadline
      ? "Add payment before trial ends."
      : "Add payment to continue using Intelligems."
    : hasLiveDeadline
      ? "Approve plan before trial ends to continue service."
      : "Approve plan to continue using Intelligems.";

  return {
    cardMessage: trialDeadlineTitle(deadline),
    cta: stripeBillingProvider
      ? "Add payment method"
      : "Approve plan in Shopify",
    secondaryText,
    actionType:
      billingProvider === "stripe"
        ? ("stripe-billing" as const)
        : ("shopify-billing" as const),
    missingBilling,
    ...getTrialNudgeUrgency(deadline),
    isTrialReminder: true,
  };
};

const billingStartsSentence = (
  deadline: TrialDeadline,
  providerName: string
): string => {
  switch (deadline.state) {
    case "running":
      return `Billing starts ${deadline.endsAtLabel} via ${providerName}.`;
    case "ends_today":
      return `Billing starts today via ${providerName}.`;
    case "ended":
    case "unknown":
      return "";
  }
};

export const activeBillingActiveTrialContent = (
  billingProvider: string,
  deadline: TrialDeadline,
  trialSub: StripeSubscriptionSummaryDto
): BillingStatusContent => {
  const stripeBillingProvider = billingProvider === "stripe";
  return {
    cardMessage: trialDeadlineTitle(deadline),
    cta: stripeBillingProvider ? "Manage payment" : "Manage plan",
    secondaryText: billingStartsSentence(
      deadline,
      stripeBillingProvider ? "Stripe" : "Shopify"
    ),
    actionType: stripeBillingProvider
      ? ("stripe-billing" as const)
      : ("shopify-billing" as const),
    missingBilling: trialSub,
    buttonColor: "info",
    buttonVariant: "outlined",
    isTrialReminder: true,
  };
};

const pendingNudgeCopy = (
  collectionMethod: SubscriptionCollectionMethod,
  missingBillingItem: MissingBillingItem
): { cardMessage: string; secondaryText: string } => {
  if (collectionMethod === "sendInvoice") {
    return {
      cardMessage: "Setup Billing Info",
      secondaryText: "Add a billing address to activate your account.",
    };
  }
  switch (missingBillingItem) {
    case "payment_method":
      return {
        cardMessage: "Add payment method",
        secondaryText:
          "Your subscription is pending. Add a payment method to activate your account.",
      };
    case "address":
      return {
        cardMessage: "Add billing address",
        secondaryText:
          "Your subscription is pending. Add your billing address to continue.",
      };
  }
};

/**
 * A reactive approval is only worth surfacing once Shopify has handed back the
 * confirmation redirect the card sends the merchant to. Before that there is
 * nothing they can do about it, and a card that asks for an approval it cannot
 * route anywhere reads as broken — so the nudge stays off screen until the
 * redirect lands. Legacy approvals carry their own redirect id and are always
 * actionable.
 */
export const isShopifyApprovalActionable = (
  pendingShopifyApproval: PendingShopifyApprovalInfo
): boolean =>
  !("approvalId" in pendingShopifyApproval) ||
  pendingShopifyApproval.confirmationRedirectAvailable;

const formatSpendingCap = ({
  amount,
  currencyCode,
}: {
  amount: number;
  currencyCode: string;
}): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

/**
 * A parked Shopify approval outranks the trial nudge, so for an org in both
 * states this is the only card that renders — which makes it responsible for
 * telling the trial story too. When a trial is in play the card takes the
 * trial framing and the deadline urgency ramp; approvals with no trial behind
 * them (an admin-created plan change for an existing customer) keep the plain
 * approval framing and its terminal styling.
 *
 * A cap raise is the exception: the merchant already approved their plan, so
 * the trial framing would ask for an approval they already gave. Shopify's
 * confirmation screen shows only the new cap, so the card names the cap and
 * why it is being raised.
 *
 * Only ever called for an approval that passed `isShopifyApprovalActionable`,
 * so the CTA is unconditional.
 */
export const pendingShopifyApprovalContent = (
  pendingShopifyApproval: PendingShopifyApprovalInfo,
  deadline: TrialDeadline,
  hadRecentTrial: boolean
): BillingStatusContent => {
  const isReactiveApproval = "approvalId" in pendingShopifyApproval;
  const shared = {
    cta: "Approve plan in Shopify",
    actionType: "shopify-approval" as const,
    pendingShopifyApproval,
    ...getTrialNudgeUrgency(deadline),
  };

  if (isReactiveApproval && pendingShopifyApproval.kind === "cap_raise") {
    const cap = formatSpendingCap(pendingShopifyApproval.requestedCap);
    return {
      ...shared,
      cardMessage: "Approve billing update",
      cta: "Approve in Shopify",
      secondaryText: `Your invoice is ready, but Shopify needs you to approve a higher spending cap (${cap}) before we can bill it. You're only charged for your plan.`,
    };
  }

  if (isTrialDeadlineAhead(deadline) || hadRecentTrial) {
    return {
      ...shared,
      cardMessage: trialDeadlineTitle(deadline),
      secondaryText: isTrialDeadlineAhead(deadline)
        ? "Approve plan before trial ends to continue service."
        : "Approve plan to continue using Intelligems.",
      isTrialReminder: true,
    };
  }

  return {
    ...shared,
    cardMessage: "Approve plan in Shopify",
    secondaryText:
      !isReactiveApproval &&
      pendingShopifyApproval.requestSource === "admin_created"
        ? "Your Intelligems contact set up a new plan. Approve it in Shopify to continue."
        : "Approve your plan in Shopify to activate your subscription.",
  };
};

const listFormat = new Intl.ListFormat("en", { type: "conjunction" });

/**
 * The modules a lapsed trial held, read from the Shopify subscription whose
 * trial the lapse refers to, for naming what choosing a plan turns back on.
 */
const lapsedTrialModuleNames = (
  org: OrganizationSummaryDto,
  trialEndsAt: number
): string[] => {
  const trialSubscription = org.stripeSubscriptionSummaries.find(
    ({ billingProvider, trialEnd }) =>
      billingProvider === "shopify" && trialEnd === trialEndsAt
  );
  return [
    ...new Set(
      (trialSubscription?.stripeProductLinks ?? []).flatMap(
        ({ stripeProduct }) => (stripeProduct ? [stripeProduct.name] : [])
      )
    ),
  ];
};

/**
 * `undefined` falls through to the choose-plan bands. `null` hides the card
 * where any card would mislead: an unresolved trial's could ask a committed
 * merchant to choose again, one beside an approval still awaiting Shopify's
 * redirect would compete with it, and an approved commitment leaves nothing to
 * do outside billing settings.
 */
export const bridgeOnlyTrialContent = ({
  org,
  isInQuietWindow,
}: {
  org: OrganizationSummaryDto;
  isInQuietWindow: boolean;
}): BillingStatusContent | null | undefined => {
  const { shopifyTrial, planCommitment, pendingShopifyApproval } = org;
  if (shopifyTrial?.state === "lapsed") {
    const moduleNames =
      planCommitment?.moduleNames ??
      lapsedTrialModuleNames(org, shopifyTrial.trialEndsAt);
    return {
      cardMessage: "Your free trial has ended",
      cta: "Choose plan",
      secondaryText: moduleNames.length
        ? `Choose a plan to turn ${listFormat.format(moduleNames)} back on.`
        : "Choose a plan to turn your modules back on.",
      actionType: "shopify-billing" as const,
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }

  if (shopifyTrial?.state === "unresolved") {
    return null;
  }

  // Checked even without a trial, since the fall-through bands say "Choose plan".
  if (!planCommitment) {
    return undefined;
  }

  const startsOn = formatDate(planCommitment.startsAt);
  const endsTrial = commitmentEndsTrial(planCommitment);
  const approvalCopy = {
    cardMessage: "Approve plan in Shopify",
    cta: "Approve in Shopify",
    secondaryText: endsTrial
      ? "Your plan is ready. Approving it in Shopify ends your free trial and starts billing right away."
      : `Your plan is ready. Approve it in Shopify to start on ${startsOn}. You won't be charged until then.`,
    buttonColor: "primary",
    buttonVariant: "contained",
    isTrialReminder: true,
  } satisfies Partial<BillingStatusContent>;

  // A pending approval outranks an approved commitment: a later commitment
  // that needs a higher cap leaves the earlier approval on the link, and the
  // merchant's next step is to approve the new one.
  if (pendingShopifyApproval) {
    if (
      isInQuietWindow ||
      !isShopifyApprovalActionable(pendingShopifyApproval)
    ) {
      return null;
    }
    return {
      ...approvalCopy,
      actionType: "shopify-approval" as const,
      pendingShopifyApproval,
    };
  }

  if (planCommitment.providerApproval === "approved") {
    return null;
  }

  // Declined or expired at Shopify: nothing to resume, so the button reopens
  // the picker on the committed plan, whose submit asks Shopify again.
  return isInQuietWindow
    ? null
    : { ...approvalCopy, actionType: "shopify-billing" as const };
};

const noActiveSubscriptionContent = (): BillingStatusContent => ({
  cardMessage: "No active subscription",
  cta: "Choose plan",
  secondaryText: "Select plan to continue using Intelligems.",
  actionType: "navigate" as const,
  buttonColor: "primary",
  buttonVariant: "contained",
});

export const useBillingStatusContent = (): BillingStatusContent | null => {
  const orgSummary = useGetOrganizationSummaryWithLoading();

  if (orgSummary.isLoading || !orgSummary.data) {
    return null;
  }

  const isInQuietWindow = isBillingNudgePopupInQuietWindow(
    orgSummary.data.org.createdAtTs
  );

  const stripeSubscriptionSummaries =
    orgSummary.data.org.stripeSubscriptionSummaries;

  const missingBillings = stripeSubscriptionSummaries.filter((ss) => {
    return (
      ["active", "trialing", "pending", "incomplete", "past_due"].includes(
        ss.status
      ) && !ss.customer.billing
    );
  });

  const pendingBillingCommand = orgSummary.data.org.pendingBillingCommand;
  if (pendingBillingCommand) {
    const { cardMessage, secondaryText } = pendingNudgeCopy(
      pendingBillingCommand.collectionMethod,
      pendingBillingCommand.missingBillingItem
    );
    return {
      cardMessage,
      cta: cardMessage,
      secondaryText,
      actionType: "stripe-billing" as const,
      delayedSubscription: {
        stripeCustomerId: pendingBillingCommand.stripeCustomerId,
        billingProvider: "stripe",
      },
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }

  const isModular =
    orgSummary.data.org.isModular ??
    isModularOrganizationSummary(orgSummary.data.org);
  if (isModular) {
    const content = bridgeOnlyTrialContent({
      org: orgSummary.data.org,
      isInQuietWindow,
    });
    if (content !== undefined) {
      return content;
    }
  }

  const trialSub = stripeSubscriptionSummaries.find(
    (s) => s.status === "trialing"
  );
  const trialDeadline = resolveTrialDeadline(
    latestTrialEnd(orgSummary.data.org)
  );

  const pendingShopifyApproval = orgSummary.data.org.pendingShopifyApproval;
  if (pendingShopifyApproval) {
    // A pending approval outranks every nudge below it, so an approval we
    // cannot route anywhere yet suppresses the card outright rather than
    // falling through — the org has a plan change in flight, and pointing them
    // at a competing billing action mid-flight is worse than staying quiet.
    if (
      isInQuietWindow ||
      !isShopifyApprovalActionable(pendingShopifyApproval)
    ) {
      return null;
    }
    return pendingShopifyApprovalContent(
      pendingShopifyApproval,
      trialDeadline,
      hadRecentTrialSubscription(orgSummary.data.org)
    );
  }

  const recoverableFailedBillingCommand =
    orgSummary.data.org.recoverableFailedBillingCommand;
  if (recoverableFailedBillingCommand) {
    return {
      cardMessage: "Add billing address",
      cta: "Add billing address",
      secondaryText:
        "Your subscription couldn’t activate. Add your billing address to continue.",
      actionType: "stripe-billing" as const,
      delayedSubscription: {
        stripeCustomerId: recoverableFailedBillingCommand.stripeCustomerId,
        billingProvider: "stripe",
      },
      recoveryCommandId: recoverableFailedBillingCommand.commandId,
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }

  // Check for delayed subscription FIRST
  if (
    orgSummary.data.org.hasDelayedSubscription &&
    orgSummary.data.org.delayedSubscriptionInfo
  ) {
    const billingProvider = "stripe";
    const sendInvoice =
      orgSummary.data.org.delayedSubscriptionInfo.collectionMethod ===
      "sendInvoice";
    return {
      cardMessage: sendInvoice ? "Setup Billing Info" : "Add payment method",
      cta: sendInvoice ? "Setup Billing Info" : "Add payment method",
      secondaryText: sendInvoice
        ? "Add billing address or payment method to activate your account."
        : "Your subscription is pending. Add a payment method to activate your account.",
      actionType: "stripe-billing" as const,
      delayedSubscription: {
        stripeCustomerId:
          orgSummary.data.org.delayedSubscriptionInfo.customerId,
        billingProvider,
      },
      buttonColor: "error",
      buttonVariant: "contained",
    };
  }

  const legacySubscriptions = orgSummary.data.org.organizationSubscriptions;

  const hasActivePausePlan = stripeSubscriptionSummaries.some(
    (ss) =>
      ss.status === "active" &&
      ss.stripeProductLinks.some((pl) =>
        pl.stripeProduct?.name?.includes("Pause Plan")
      )
  );
  const hasActiveNonPausePlan = stripeSubscriptionSummaries.some(
    (ss) =>
      ss.status === "active" &&
      ss.stripeProductLinks.some(
        (pl) => !pl.stripeProduct?.name?.includes("Pause Plan")
      )
  );
  const isPausedPlan = hasActivePausePlan && !hasActiveNonPausePlan;
  if (isPausedPlan) {
    return {
      cardMessage: `Your plan is paused`,
      cta: "Manage plan",
      secondaryText: `Resume your plan to continue using Intelligems.`,
      actionType: "navigate" as const,
      missingBilling: null,
      buttonColor: "warning",
      buttonVariant: "contained",
    };
  }

  const activeSub = stripeSubscriptionSummaries.find(
    (s) => s.status === "active"
  );

  const pendingSub = stripeSubscriptionSummaries.find(
    (s) => s.status === "pending"
  );

  const incompleteSub = stripeSubscriptionSummaries.find(
    (s) => s.status === "incomplete"
  );

  const pastDueSub = stripeSubscriptionSummaries.find(
    (s) => s.status === "past_due"
  );

  const recentTrialSubscription = hadRecentTrialSubscription(
    orgSummary.data.org
  );
  const activeLegacySub = legacySubscriptions.find(
    (sub) => sub.status === "active"
  );

  if (activeLegacySub) {
    return null;
  }

  if (activeSub || incompleteSub || pendingSub) {
    if (missingBillings.length === 0) {
      return null;
    } else {
      const billingProvider = (activeSub || pendingSub || incompleteSub)
        ?.billingProvider;

      if (billingProvider) {
        return trialEndedContent(
          billingProvider,
          recentTrialSubscription,
          missingBillings
        );
      }
    }
  } else if (pastDueSub) {
    return pastDueContent(pastDueSub?.billingProvider, missingBillings);
  } else if (trialSub) {
    if (missingBillings.length > 0) {
      if (
        trialSub.billingProvider === "shopify" &&
        isTrialDeadlineAhead(trialDeadline) &&
        isModular
      ) {
        return chooseTrialPlanContent(trialDeadline, missingBillings);
      }
      return noBillingActiveTrialContent(
        trialSub.billingProvider,
        trialDeadline,
        missingBillings
      );
    }

    return activeBillingActiveTrialContent(
      trialSub.billingProvider,
      trialDeadline,
      trialSub
    );
  }

  if (recentTrialSubscription) {
    return trialEndedNoBillingContent();
  }

  return isInQuietWindow ? null : noActiveSubscriptionContent();
};
